import { mediaUrl } from "@/lib/helpers";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Radio, Tv, Mic2, Martini, Disc3, Trophy, Crown, Music, Clapperboard, Video, Sparkles, Scissors, Users2, Award, PersonStanding, Megaphone, Smartphone, Guitar } from "lucide-react";
import { api } from "@/lib/api";
import { Sparkline } from "@/components/wei/Ranking";
import { SectionHeading, Skeleton } from "@/components/wei/Section";
import { VerifiedBadge, DemoTag } from "@/components/wei/Badges";

const ICONS = { "wei-radio-index": Radio, "wei-tv-index": Tv, "wei-podcast-index": Mic2, "wei-lounge-index": Martini, "wei-nightlife-index": Disc3, "wei-goat-100": Crown, "wei-song-index": Music, "wei-music-video-index": Video, "wei-actor-index": Clapperboard, "wei-model-index": Sparkles, "wei-fashion-designer-index": Scissors, "wei-modelling-agency-index": Users2, "wei-modelling-contest-index": Award, "wei-dancer-index": PersonStanding, "wei-influencer-index": Megaphone, "wei-creator-index": Smartphone, "wei-musician-index": Guitar };

export function useOverview() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/rankings/overview").then((r) => setData(r.data)).catch(() => setData([])); }, []);
  return data;
}

export function IndexTicker({ data }) {
  if (!data?.length) return <div className="h-10 bg-ink" />;
  const items = [...data, ...data];
  return (
    <div data-testid="index-ticker" className="bg-ink text-white overflow-hidden border-y border-gold/30">
      <div className="ticker-track py-2.5">
        {items.map((r, i) => (
          <Link key={`${r.slug}-${i}`} to={`/rankings/${r.slug}`} className="flex items-center gap-3 px-6 border-r border-white/10 whitespace-nowrap hover:text-gold-light transition-colors">
            <span className="eyebrow text-gold text-[10px]">{r.name}</span>
            {r.top[0] && <span className="text-xs font-medium">#1 {r.top[0].name}</span>}
            {r.mover && <span className={`text-[11px] font-semibold ${r.mover.movement > 0 ? "text-emerald-400" : "text-white/50"}`}>{r.mover.movement > 0 ? "▲" : "—"} {r.mover.name} {r.mover.movement > 0 ? `+${r.mover.movement}` : ""}</span>}
          </Link>
        ))}
      </div>
    </div>
  );
}

export function IndexCard({ r, large = false }) {
  const Icon = ICONS[r.slug] || Trophy;
  return (
    <Link to={`/rankings/${r.slug}`} data-testid={`index-card-${r.slug}`} className={`group relative overflow-hidden rounded-md bg-ink text-white block ${large ? "min-h-[360px]" : "min-h-[300px]"}`}>
      <img src={mediaUrl(r.image)} alt="" className="absolute inset-0 w-full h-full object-cover opacity-60 transition-transform duration-700 group-hover:scale-105" loading="lazy" />
      <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/70 to-ink/10" />
      <div className="relative h-full flex flex-col justify-between p-5">
        <div className="flex items-start justify-between">
          <span className="inline-flex items-center gap-1.5 bg-white/10 backdrop-blur-sm px-2.5 py-1 rounded-sm eyebrow text-[10px] text-gold-light"><Icon className="w-3.5 h-3.5" /> {r.family === "professional" ? (r.entity === "work" ? "Works" : "Professional") : r.family === "institutional" ? "Institutional" : r.family === "goat" ? "GOAT" : r.family}</span>
          <span className="text-[11px] text-white/70">{r.count} classified</span>
        </div>
        <div>
          <h3 className={`font-serif font-semibold leading-tight ${large ? "text-3xl" : "text-2xl"}`}>{r.name}</h3>
          <ol className="mt-3 space-y-1.5">
            {r.top.map((t) => (
              <li key={t.slug} className="flex items-center gap-2 text-sm">
                <span className="font-serif text-gold font-bold w-6">#{t.rank}</span>
                <img src={mediaUrl(t.image)} alt="" className="w-6 h-6 rounded-sm object-cover" />
                <span className="truncate inline-flex items-center gap-1">{t.name} <VerifiedBadge status={t.verification_status} compact /></span>
                {t.subtitle && <span className="hidden xl:inline text-[10px] text-white/50 truncate max-w-[110px]">{t.artist_name}</span>}
                <span className={`ml-auto text-[11px] font-semibold ${t.movement > 0 ? "text-emerald-400" : t.movement < 0 ? "text-red-300" : "text-white/40"}`}>{t.movement > 0 ? `▲${t.movement}` : t.movement < 0 ? `▼${-t.movement}` : "—"}</span>
              </li>
            ))}
          </ol>
          <div className="flex items-center justify-between mt-4 pt-3 border-t border-white/10">
            <Sparkline data={r.index_trend.map((v) => -v)} color="#E8C879" width={90} height={22} />
            <span className="text-xs font-medium text-gold-light inline-flex items-center gap-1 group-hover:gap-2 transition-all">Open index <ArrowRight className="w-3.5 h-3.5" /></span>
          </div>
        </div>
      </div>
    </Link>
  );
}

const FEATURED = ["wei-global-100", "wei-goat-100", "wei-actor-index", "wei-influencer-index", "wei-model-index", "wei-song-index", "wei-music-video-index", "wei-dancer-index", "wei-fashion-designer-index", "wei-radio-index", "wei-nightlife-index", "wei-tv-index", "wei-podcast-index", "wei-presenter-index", "wei-modelling-agency-index", "wei-modelling-contest-index", "wei-lounge-index", "wei-creator-index", "wei-musician-index"];

export function ClassificationUniverse({ data }) {
  const cards = data ? FEATURED.map((s) => data.find((d) => d.slug === s)).filter(Boolean) : [];
  return (
    <section data-testid="classification-universe" className="container-wei py-12">
      <SectionHeading eyebrow="The classification universe" title="WEI Classifications" to="/rankings" linkLabel="All indices" id="classifications" />
      <p className="-mt-3 mb-6 text-sm text-charcoal max-w-2xl">Evidence-based classification of talent, radio, television, night clubs, lounges, podcasts and presenters — the world's entertainment record, ranked. <DemoTag /></p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.length ? cards.map((r, i) => <div key={r.slug} className={`fade-up ${i === 0 ? "sm:col-span-2 lg:col-span-2" : ""}`} style={{ animationDelay: `${i * 70}ms` }}><IndexCard r={r} large={i === 0} /></div>) : Array(11).fill(null).map((_, i) => <Skeleton key={i} className="h-[300px]" />)}
      </div>
    </section>
  );
}
